import { type Maybe, type ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { virtualize } from '@adornis/chemistry/directives/when-seen.js';
import { SubscriptionPagination } from '@adornis/pagination/paginatable.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { type CampusLesson } from '@chancenwerk/campus-learning-content/entities/CampusLesson.js';
import { CampusArea, CampusSection } from '@chancenwerk/campus-learning-content/entities/CampusSection.js';
import { CampusSectionFilter } from '@chancenwerk/campus-learning-content/entities/CampusSectionFilter.js';
import '@chancenwerk/design/components/cr-campus-loader.js';
import '@chancenwerk/design/components/cr-card.js';
import '@chancenwerk/design/components/cr-link.js';
import { type ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { combineLatest, distinctUntilChanged, filter } from 'rxjs';

import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { LearningContentAPI } from '@chancenwerk/campus-learning-content/api/LearningContentAPI.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { CampusRoutes } from '../routes.js';
import './section-lesson-list/campus-component-section-card.js';

/**
 * @customElement campus-component-section-lesson-list
 */
@customElement('campus-component-section-lesson-list')
export class CampusComponentSectionLessonList extends ChemistryLitElement {
  @property() 'custom-title': string = 'Lektion aussuchen';
  @property({ type: Boolean }) 'dark-cards' = false;
  @property({ type: Boolean }) condensed = false;

  @state() public markedLessonIDs = new RXController<string[]>(this, [] as string[]);

  @property({ attribute: false }) public mode: 'play' | 'edit' | 'custom' = 'play';
  @property({ attribute: false }) public area = new RXController(this, CampusArea.STUDENTS);
  @property({ attribute: false }) public selector = new RXController(this, 'all');
  @property({ attribute: false }) public showSearchBar = new RXController(this, true);

  @state() private readonly _currentUser = new RXController(
    this,
    currentUser<ChancenwerkUser>().pipe(
      distinctUntilChanged((valueOne, valueTwo) => {
        if (this.selector.value === 'assigned') {
          return false;
        } else {
          return valueOne === valueTwo;
        }
      }),
    ),
  );

  private readonly _sectionsPaginated = new SubscriptionPagination<
    CampusSection,
    Array<CampusSectionFilter | undefined>
  >(
    LearningContentAPI.subscribeSectionsByFilter.bind(CampusSection),
    [new CampusSectionFilter({})],
    {
      name: 1,
      subjects: 1,
      hiddenFromStudents: 1,
      lessonIDs: 1,
      lessons: {
        name: 1,
        hiddenFromStudents: 1,
        lessonIDsDependingOn: 1,
        sortingOrder: 1,
        exerciseIDs: 1,
      },
    },
    2,
    2,
  );

  @state() private readonly _sections = new RXController(this, this._sectionsPaginated.data);

  @state() private readonly _sectionsLoading = new RXController(this, this._sectionsPaginated.loadingInitially);

  // Filtering properties
  @state() public userCheckedSubjects = new RXController<string[]>(this, []);
  @state() public userHideSubjects = new RXController<string[]>(this, []);
  @state() public userCheckedGrades = new RXController<number[]>(this, []);
  @state() public userSearchText = new RXController<string>(this, '');
  @state() public showOnlyMarked = new RXController<boolean>(this, false);
  @state() public userHiddenFromStudents = new RXController<Maybe<boolean>>(this, null);

  private _size = new WindowSizeController(this);

  override connectedCallback(): void {
    super.connectedCallback();

    combineLatest([
      this.userCheckedSubjects.observable,
      this.userCheckedGrades.observable,
      this.userHideSubjects.observable,
      this.userSearchText.observable,
      this.userHiddenFromStudents.observable,
      this.selector.observable,
      this._currentUser.observable,
      this.area.observable,
      this.showOnlyMarked.observable,
    ])
      .pipe(filter(Boolean))
      .subscribe(
        ([
          checkedSubjects,
          checkedGrades,
          hideSubjects,
          searchText,
          hiddenFromStudents,
          selector,
          user,
          area,
          onlyMarked,
        ]) => {
          this._sectionsPaginated.updateParameters(
            new CampusSectionFilter({
              userSearchText: searchText,
              userCheckedSubjects: checkedSubjects.filter(Boolean),
              userCheckedGrades: checkedGrades.filter(Boolean),
              userHideSubjects: hideSubjects.filter(Boolean),
              hiddenFromStudents,
              userIsGrade:
                user?._class === Student._class ? (user as Student).schoolInformation.jahrgangsstufe : undefined,
              showAssignedLessons: selector === 'assigned',
              userID: user ? user._id : '',
              playMode: this.mode === 'play',
              lessonIDs: onlyMarked ? this.markedLessonIDs.value : undefined,
              area,
            }),
          );
        },
      );
  }

  @state() public hideLessonsIDs = new RXController<string[]>(this, []);

  override render() {
    if (this.condensed) {
      return html` <cr-flex space="sm"> ${this._getSectionList()} </cr-flex> `;
    }

    return html`
      <cr-flex space="md" w100>
        ${this.mode !== 'edit' && this.showSearchBar.value
          ? html`
              <cr-flex
                ${css({
                  position: 'sticky',
                  top: '0',
                  background: 'white',
                  zIndex: '1',
                })}
                space="sm"
              >
                <cr-text> ${this['custom-title']} </cr-text>
                <cr-input
                  ${css({ selfAlign: 'center', justifySelf: 'center' })}
                  @value-changed=${(e: ValueEvent<string>) => {
                    if (this.userSearchText.value === e.detail.value) return;
                    this.userSearchText.next(e.detail.value);
                  }}
                  placeholder="Suchen"
                  icon="search"
                  flex
                ></cr-input>
                ${this.mode === 'custom'
                  ? html` <cr-checkbox
                      label="Nur hinzugefügte Anzeigen"
                      @value-picked=${(e: ValueEvent<boolean>) => {
                        this.showOnlyMarked.next(e.detail.value);
                      }}
                    ></cr-checkbox>`
                  : nothing}
              </cr-flex>
            `
          : nothing}
        ${this._sectionsLoading.value
          ? html` <cr-flex center crossaxis-center flex> <cr-campus-loader></cr-campus-loader> </cr-flex> `
          : nothing}
        ${!this._sections.value?.length && !this._sections.isLoading && !this._sectionsLoading.value
          ? html`
              <cr-flex center crossaxis-center flex>
                <cr-text>
                  ${this.selector.value === 'all'
                    ? 'Es sind keine Themenbereiche für diese Filtereinstellungen eingetragen.'
                    : 'Für dich sind momentan keine Lektionen ausgewählt.'}
                </cr-text>
              </cr-flex>
            `
          : nothing}

        <cr-grid
          columns=${this._size.tablet || this._size.mobile ? '1fr' : '1fr 1fr'}
          space="md"
          ${css({
            display: this._sections.isLoading || this._sections.value?.length === 0 ? 'none' : 'grid',
            alignItems: 'stretch',
            padding: '0 2px', // Avoids a overflow hidding card border
          })}
        >
          ${this._getSectionList()}
        </cr-grid>
      </cr-flex>
    `;
  }

  private _getSectionList() {
    return html`
      ${repeat(
        this.condensed
          ? (this._sections.value ?? []).slice(0, 2)
          : (this._sections.value ?? []).filter(section => {
              if (this.mode !== 'play') return true;
              if (section.hiddenFromStudents) return false;

              const lessons = this._filterLessons(section.lessons ?? [], section);
              return lessons.length > 0;
            }),
        section => section._id,
        (section, index) => {
          return html`${virtualize(
            html`
              <campus-component-section-card
                ${css({ height: '100%', display: 'block' })}
                .showOnlyAssigned=${this.selector.value === 'assigned'}
                .sectionID=${section._id}
                .mode=${this.mode}
                .area=${this.area.value}
                .userSearchText=${this.userSearchText.value}
                .userHiddenFromStudents=${this.userHiddenFromStudents.value}
                .markedLessonIDs=${this.markedLessonIDs.value}
                .hideLessonsIDs=${this.hideLessonsIDs.value}
                .showOnlyMarked=${this.showOnlyMarked.value}
                ?condensed=${this.condensed}
                @sectionClicked=${async e => {
                  const event = new CustomEvent('sectionClicked', {
                    detail: {
                      section: e.detail.section,
                    },
                  });
                  this.dispatchEvent(event);
                }}
                @lessonClicked=${async e => {
                  const event = new CustomEvent('lessonClicked', {
                    detail: {
                      lesson: e.detail.lesson,
                      section: e.detail.section,
                    },
                  });
                  this.dispatchEvent(event);
                }}
              ></campus-component-section-card>
            `,
            oldHeight => html` <div ${css({ height: oldHeight + 'px' })}></div> `,
            {
              dontUnload: true,
              onRender: () => {
                // The current rendered item is near the end of the list && we havent already loaded the new items
                if (
                  index === (this._sections.value ?? []).length - 2 &&
                  this._sectionsPaginated.totalRequested.getValue() < index + 4
                ) {
                  this._sectionsPaginated.next();
                }
              },
            },
          )} `;
        },
      )}
      ${this.condensed
        ? html`
            <cr-flex ${css({ marginTop: this.spacing.md })} space="sm">
              <cr-text>
                ${(this._sections.value ?? []).length - 2 > 1
                  ? `Und ${(this._sections.value ?? []).length - 2} weitere Themenbereiche...`
                  : (this._sections.value ?? []).length - 2 == 1
                  ? 'Und ein weiterer Themenbereich...'
                  : ''}
              </cr-text>
              <cr-text>
                Schau dir
                <cr-link href=${CampusRoutes.yourLessons}>
                  alle für dich ausgewählten Lektionen und Themenbereiche
                </cr-link>
                an.
              </cr-text>
            </cr-flex>
          `
        : nothing}
    `;
  }

  private _filterLessons(localLessons: CampusLesson[], section: CampusSection): CampusLesson[] {
    if (this.showOnlyMarked.value) {
      localLessons = localLessons.filter(lesson => this.markedLessonIDs.value.includes(lesson._id));
    }

    // Textfilter for searchbar
    if ((this.userSearchText.value || '').length) {
      localLessons = section.name.toLocaleLowerCase().includes(this.userSearchText.value.toLocaleLowerCase())
        ? localLessons
        : localLessons.filter(lesson =>
            lesson.name.toLocaleLowerCase().includes(this.userSearchText.value.toLocaleLowerCase()),
          );
    }

    // Filter lessons if they are hidden from students
    if (this.mode === 'play') {
      localLessons = localLessons.filter(lesson => !lesson.hiddenFromStudents);
    }

    if (typeof this.userHiddenFromStudents.value === 'boolean') {
      localLessons = localLessons.filter(lesson => {
        return lesson.hiddenFromStudents === this.userHiddenFromStudents.value;
      });
    }

    // Filter only assigned lessons
    localLessons = localLessons.filter(lesson => !this.hideLessonsIDs.value.includes(lesson._id));

    return localLessons;
  }
}
